import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CommercialEPCBristol = () => {
  const faqs = [
    {
      question: "How much does a Commercial EPC cost in Bristol?",
      answer: "Commercial EPC pricing in Bristol varies based on property type, size and complexity. A small retail unit or office (up to 500 sq m) typically costs £180-£400. Medium-sized warehouses or industrial units (500-2000 sq m) range from £400-£800. Larger commercial premises, multi-let buildings or complex hotels may cost £800-£2000+. Contact us for a tailored Bristol quote."
    },
    {
      question: "How long does a Commercial EPC take in Bristol?",
      answer: "The on-site survey for a Commercial EPC in Bristol typically takes 1-3 hours depending on property size and complexity. Once the survey is complete, we process the data and deliver your certificate within 5-7 working days. For urgent Bristol commercial transactions, expedited turnaround may be available - call 01392 980077 to discuss."
    },
    {
      question: "Do you cover all areas of Bristol?",
      answer: "Yes, we cover all Bristol postcodes including BS1 (city centre), BS2-BS10 (central, east and north Bristol), BS13-BS16 (south Bristol, Hartcliffe, Fishponds), and BS20+ (outer Bristol, Portishead). We also cover North Somerset, South Gloucestershire and surrounding areas within the Bristol travel-to-work area."
    },
    {
      question: "What buildings require a Commercial EPC in Bristol?",
      answer: "All non-domestic buildings in Bristol require an EPC when sold, rented or constructed. This includes offices, shops, warehouses, industrial units, hotels, restaurants, pubs, schools, leisure centres, care homes and any other commercial or public building. EPCs are also required for Bristol buildings undergoing major refurbishment."
    },
    {
      question: "What is the minimum EPC rating for renting commercial property in Bristol?",
      answer: "Since April 2023, it is illegal to rent out commercial property in Bristol (or anywhere in England and Wales) with an EPC rating below E, unless you have a valid exemption. Our Non-Domestic Energy Advice Reports can help Bristol landlords improve ratings and achieve MEES compliance."
    }
  ];

  const bristolAreas = [
    "BS1 - Bristol City Centre, Harbourside, Temple Meads",
    "BS2-BS4 - St Pauls, Easton, Brislington, Knowle",
    "BS5-BS7 - Eastville, Fishponds, Horfield, Bishopston",
    "BS8-BS9 - Clifton, Redland, Stoke Bishop, Westbury-on-Trym",
    "BS10-BS11 - Southmead, Avonmouth, Shirehampton",
    "BS13-BS16 - Hartcliffe, Withywood, Hengrove, Stockwood, Downend",
    "BS20-BS49 - Portishead, Clevedon, North Somerset"
  ];

  return (
    <>
      <Helmet>
        <title>Commercial EPC Bristol | Non-Domestic Energy Certificates | NDEA Qualified | Fast Turnaround</title>
        <meta name="description" content="Professional Commercial EPC assessments in Bristol from £180. NDEA qualified assessors covering all BS postcodes. Offices, warehouses, retail, hotels. Fast 5-7 day delivery. Call 01392 980077." />
        <meta name="keywords" content="commercial EPC Bristol, non-domestic EPC Bristol, office EPC Bristol, warehouse EPC Bristol, retail EPC Bristol, hotel EPC Bristol, NDEA Bristol, commercial energy certificate Bristol, BS1 EPC, business EPC Bristol" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/commercial-epc-bristol" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Commercial Energy Performance Certificate (EPC)",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk"
            },
            "areaServed": {
              "@type": "City",
              "name": "Bristol"
            },
            "offers": {
              "@type": "Offer",
              "price": "180",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "minPrice": "180"
              }
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Commercial Property Owners, Landlords, Business Owners in Bristol"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Serving All Bristol & North Somerset</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Commercial EPC Assessments in Bristol
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Professional Non-Domestic Energy Performance Certificates for Bristol businesses. NDEA qualified assessors covering offices, warehouses, retail, hotels across all BS postcodes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking">
                  <Button className="text-white hover:opacity-90 px-8 py-6 text-lg rounded-full shadow-lg" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                    Book Bristol Commercial EPC
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">NDEA Qualified</h3>
                  <p className="text-sm text-gray-600">Elmhurst accredited commercial assessors</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">5-7 Day Delivery</h3>
                  <p className="text-sm text-gray-600">Fast turnaround for Bristol businesses</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Regional Coverage</h3>
                  <p className="text-sm text-gray-600">All Bristol & North Somerset postcodes</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Building2 className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £180</h3>
                  <p className="text-sm text-gray-600">Competitive commercial EPC pricing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Commercial EPC Services in Bristol</h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional Commercial Energy Performance Certificate (EPC) assessments for non-domestic buildings throughout Bristol, North Somerset, and South Gloucestershire. With extensive experience surveying Bristol's diverse commercial property stock—from modern office blocks in Temple Meads to industrial warehouses in Avonmouth and retail units on Gloucester Road—we deliver accurate, compliant EPCs tailored to Bristol businesses.
                </p>

                <p>
                  A Commercial (Non-Domestic) EPC is legally required whenever you sell, rent, or construct a commercial building in Bristol. The certificate rates your property's energy efficiency on a scale from A (most efficient) to G (least efficient) and provides a detailed recommendations report outlining practical measures to improve energy performance, reduce operating costs, and meet MEES (Minimum Energy Efficiency Standards) compliance for commercial lettings.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Choose Energy Performance Direct for Your Bristol Commercial EPC?</h3>

                <p>
                  Operating across the West Country since 2009, we have built a strong reputation for reliable, professional Commercial EPC assessments in Bristol and the wider region. All our commercial assessors are NDEA (Non-Domestic Energy Assessor) qualified and accredited through Elmhurst Energy, ensuring your EPC meets all legal requirements and is accepted by solicitors, commercial agents, and Building Control across Bristol and beyond.
                </p>

                <p>
                  We understand the operational pressures facing Bristol businesses and work flexibly to minimize disruption. Assessments can be scheduled outside normal business hours, at weekends, or during periods of lower activity to avoid impacting your operations. Our local presence in the South West means we can respond quickly to urgent Bristol commercial property transactions.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Bristol Areas We Cover for Commercial EPCs</h3>

                <p>
                  We provide Commercial EPC assessments across all Bristol postcodes and surrounding areas. Our assessors know Bristol's commercial districts well, from the waterfront developments around Harbourside to the industrial estates in Avonmouth and Portbury, the retail centers in Cabot Circus and Cribbs Causeway, and the office districts in Temple Quarter and Clifton.
                </p>

                <div className="bg-white border border-gray-200 rounded-lg p-6 my-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Bristol & Regional Postcodes Covered:</h4>
                  <ul className="space-y-2">
                    {bristolAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{area}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600 mt-4">
                    We also cover Bath, Weston-super-Mare, Clevedon, Nailsea, Yate, and other towns within the Bristol travel-to-work area.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Types of Commercial Property We Assess in Bristol</h3>

                <p>
                  Our NDEA qualified assessors have experience surveying a wide range of commercial and non-domestic buildings across Bristol:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Offices:</strong> City centre, business parks, serviced offices (BS1, BS2, BS3, Temple Quarter)</li>
                  <li><strong>Retail:</strong> High street shops, shopping centers, supermarkets (Cabot Circus, Broadmead, Gloucester Road)</li>
                  <li><strong>Warehouses:</strong> Distribution centers, logistics hubs, storage facilities (Avonmouth, Portbury, Filton)</li>
                  <li><strong>Industrial:</strong> Factories, workshops, manufacturing units (St Philips, Lawrence Hill, Bedminster)</li>
                  <li><strong>Hotels:</strong> City centre hotels, budget chains, boutique accommodation (Harbourside, Clifton, airport area)</li>
                  <li><strong>Restaurants & Pubs:</strong> Cafes, restaurants, bars, nightclubs across Bristol</li>
                  <li><strong>Leisure:</strong> Gyms, sports centers, cinemas, theatres</li>
                  <li><strong>Education:</strong> Schools, colleges, training centers, nurseries</li>
                  <li><strong>Healthcare:</strong> Clinics, dental surgeries, care homes, medical centers</li>
                  <li><strong>Public buildings:</strong> Community centers, libraries, council offices</li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">The Commercial EPC Assessment Process</h3>

                <p>
                  A Commercial EPC assessment in Bristol is more detailed than a Domestic EPC and tailored to the specific building type and usage. An NDEA qualified assessor will visit your Bristol property and conduct a comprehensive survey, typically taking 1-3 hours depending on size and complexity. The assessment includes:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Measuring building dimensions and calculating floor areas</li>
                  <li>Recording construction type, wall insulation, roof insulation</li>
                  <li>Assessing glazing type and areas (single, double glazed windows, curtain walling)</li>
                  <li>Evaluating heating systems (gas boilers, electric heating, heat pumps, district heating)</li>
                  <li>Recording hot water systems and controls</li>
                  <li>Noting lighting type and control systems (LED, fluorescent, occupancy sensors)</li>
                  <li>Assessing ventilation and air conditioning systems</li>
                  <li>Identifying renewable energy installations (solar PV, biomass, wind)</li>
                  <li>Photographing building features for compliance records</li>
                </ul>

                <p>
                  Once the survey is complete, we input the data into approved commercial EPC software (iSBEM or DSM for England and Wales). The software calculates the building's energy performance, generates the EPC certificate, and produces a detailed recommendations report showing cost-effective measures to improve energy efficiency. Your Commercial EPC is lodged with the official Non-Domestic EPC register and delivered to you within 5-7 working days.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Commercial EPC Pricing for Bristol Properties</h3>

                <p>
                  Commercial EPC pricing in Bristol depends on property type, size, and complexity. Indicative pricing is as follows:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Small offices/retail (up to 500 sq m):</strong> £180-£400</li>
                  <li><strong>Medium warehouses/industrial (500-2000 sq m):</strong> £400-£800</li>
                  <li><strong>Large warehouses/distribution centers (2000+ sq m):</strong> £800-£1500+</li>
                  <li><strong>Hotels (budget/mid-range):</strong> £500-£1200 depending on size and HVAC complexity</li>
                  <li><strong>Restaurants/pubs:</strong> £350-£600 depending on size and kitchen equipment</li>
                  <li><strong>Multi-let commercial buildings:</strong> Price on application (depends on number of units)</li>
                </ul>

                <p>
                  All prices include the on-site survey, data processing, EPC certificate, recommendations report, and lodgement with the official register. Contact us on 01392 980077 or email info@energyperformancedirect.co.uk with details of your Bristol commercial property for an accurate quote.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">MEES Compliance for Bristol Commercial Landlords</h3>

                <p>
                  Since April 2023, it is illegal to rent out commercial property in Bristol with an EPC rating below E, unless you have a valid exemption registered. This applies to all new leases and lease renewals. Many Bristol commercial landlords with older properties (pre-2000s warehouses, period office conversions, traditional retail units) have EPC ratings of F or G and must take action to improve energy performance or risk financial penalties.
                </p>

                <p>
                  Our <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 underline">Commercial Energy Advice Reports</Link> provide Bristol landlords with clear, practical guidance on improving EPC ratings, estimating costs of improvement measures, and navigating the MEES regulatory framework. We can also recommend accredited contractors and energy consultants to implement improvement works.
                </p>

                <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6 my-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Need Lease Plans for Your Bristol Commercial Property?</h4>
                  <p className="text-gray-700 mb-4">
                    Many Bristol commercial property transactions require accurate lease plans showing demised areas, shared spaces, and common parts. We provide professional <Link to="/lease-plans-west-country" className="text-cyan-600 hover:text-cyan-700 underline font-semibold">Lease Plans across the West Country</Link> drawn to Land Registry standards and suitable for commercial leases and property sales.
                  </p>
                  <Link to="/lease-plans-west-country">
                    <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                      View Lease Plan Services
                    </Button>
                  </Link>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Book Your Bristol Commercial EPC Today</h3>

                <p>
                  Ready to book a Commercial EPC for your Bristol property? Use our <Link to="/booking" className="text-green-600 hover:text-green-700 underline font-semibold">online booking system</Link> or call us on 01392 980077 to discuss your requirements. We typically offer survey appointments within 5-7 working days across all Bristol postcodes, with your EPC certificate delivered within 5-7 working days of the survey.
                </p>

                <p>
                  For urgent Bristol commercial transactions (completion deadlines, lease exchanges, Building Control sign-offs), please call us to discuss expedited service. We understand the commercial pressures and work to accommodate tight timescales wherever possible.
                </p>
              </div>

              {/* FAQ Section */}
              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions - Bristol Commercial EPCs</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-16 rounded-2xl p-8 text-white text-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                <h2 className="text-3xl font-bold mb-4">Ready to Book Your Bristol Commercial EPC?</h2>
                <p className="text-lg mb-6">
                  Professional Non-Domestic EPCs across Bristol & North Somerset. Call 01392 980077 or book online now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                      Book Online Now
                    </Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>

              {/* Industrial EPC Cross-Links */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Industrial and Warehouse EPCs in Bristol</h3>
                <p className="text-gray-700 mb-6">
                  Need a specialist industrial or warehouse EPC in Bristol? We cover Avonmouth, Severnside, Portbury and all major Bristol industrial estates for warehouse, factory and workshop assessments.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                    <p className="text-sm text-gray-600">Warehouses, factories and workshops</p>
                  </Link>
                  <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Warehouse EPCs</h3>
                    <p className="text-sm text-gray-600">Distribution centres and logistics hubs</p>
                  </Link>
                  <Link to="/mees-commercial-buildings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">MEES Compliance</h3>
                    <p className="text-sm text-gray-600">Minimum energy efficiency standards guide</p>
                  </Link>
                </div>
              </div>

              {/* Related Services */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Services in Bristol</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Lease Plans</h4>
                      <p className="text-sm text-gray-600 mb-4">Professional lease plans for Bristol commercial properties</p>
                      <Link to="/lease-plans-west-country">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Energy Advice Reports</h4>
                      <p className="text-sm text-gray-600 mb-4">MEES compliance guidance for Bristol landlords</p>
                      <Link to="/services/commercial_advice">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">All Commercial Services</h4>
                      <p className="text-sm text-gray-600 mb-4">View our full range of services</p>
                      <Link to="/services">
                        <Button variant="outline" className="w-full">View Services</Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>

            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPCBristol;
